/**
 * OperationsAdminPage — consolidated admin panel for Driver Payouts,
 * Gift Cards, and Monthly Raffle. Kept as a single file to reduce navigation
 * bloat until each panel grows enough to deserve its own page.
 */
import React, { useEffect, useState } from 'react';
import { CreditCard, Ticket, Gift, DollarSign, Check, Send, Loader2, Trophy } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const OperationsAdminPage = () => {
  const [tab, setTab] = useState('payouts');
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="ops-admin-page">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Operations</h1>
        <p className="text-sm text-slate-500">Driver payouts, gift cards, and monthly raffle.</p>
      </header>
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'payouts', icon: CreditCard, label: 'Driver payouts' },
          { id: 'giftcards', icon: Gift, label: 'Gift cards' },
          { id: 'raffle', icon: Ticket, label: 'Raffle' },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`ops-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === 'payouts' && <PayoutsPanel />}
      {tab === 'giftcards' && <GiftCardsPanel />}
      {tab === 'raffle' && <RafflePanel />}
    </div>
  );
};

// ---------- Driver payouts ----------
const PayoutsPanel = () => {
  const [payouts, setPayouts] = useState([]);
  const [status, setStatus] = useState('pending');
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/driver-payouts', { params: { status } });
      setPayouts(data.payouts || []);
      setSelected(new Set());
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [status]);
  const toggleAll = () => {
    if (selected.size === payouts.length) setSelected(new Set());
    else setSelected(new Set(payouts.map((p) => p.id)));
  };
  const bulk = async (action) => {
    if (!selected.size) return;
    const url = action === 'approve' ? '/admin/driver-payouts/approve' : '/admin/driver-payouts/mark-paid';
    try {
      const { data } = await api.post(url, { payout_ids: Array.from(selected) });
      toast.success(`${data.approved ?? data.paid} row(s) ${action === 'approve' ? 'approved' : 'marked paid'}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center gap-2 mb-3">
        {['pending','approved','paid','all'].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            data-testid={`payout-filter-${s}`}
            className={`px-3 h-8 rounded-full text-xs font-medium border ${status===s?'bg-slate-900 text-white border-slate-900':'bg-white text-slate-700 border-slate-200'}`}>
            {s}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {status === 'pending' && (
            <Button size="sm" onClick={() => bulk('approve')} disabled={!selected.size} data-testid="payout-approve-btn">
              <Check className="h-3 w-3 mr-1" /> Approve ({selected.size})
            </Button>
          )}
          {(status === 'approved' || status === 'pending') && (
            <Button size="sm" onClick={() => bulk('paid')} disabled={!selected.size} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="payout-markpaid-btn">
              <Send className="h-3 w-3 mr-1" /> Mark paid ({selected.size})
            </Button>
          )}
        </div>
      </div>
      {loading ? <div className="text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin inline mr-1"/> Loading…</div>
        : payouts.length === 0 ? <div className="text-sm text-slate-400">No {status} payouts.</div>
        : (
          <table className="w-full text-sm" data-testid="payouts-table">
            <thead className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
              <tr>
                <th className="pb-2 pl-1"><input type="checkbox" checked={selected.size===payouts.length} onChange={toggleAll} /></th>
                <th className="pb-2">Driver</th><th className="pb-2">Order</th><th className="pb-2">Amount</th><th className="pb-2">CAD</th><th className="pb-2">Status</th><th className="pb-2">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {payouts.map((p) => (
                <tr key={p.id}>
                  <td className="py-2 pl-1"><input type="checkbox" checked={selected.has(p.id)} onChange={() => {
                    const next = new Set(selected); if (next.has(p.id)) next.delete(p.id); else next.add(p.id); setSelected(next);
                  }} /></td>
                  <td className="py-2 font-mono text-xs">{p.driver_id}</td>
                  <td className="py-2 font-mono text-xs">{p.order_id}</td>
                  <td className="py-2">{p.amount} <span className="text-xs text-slate-500">{p.currency}</span></td>
                  <td className="py-2">CA${p.amount_cad}</td>
                  <td className="py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 capitalize">{p.status}</span></td>
                  <td className="py-2 text-[11px] text-slate-500">{new Date(p.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
    </section>
  );
};

// ---------- Gift cards ----------
const GiftCardsPanel = () => {
  const [amount, setAmount] = useState('25');
  const [currency, setCurrency] = useState('CAD');
  const [vendorId, setVendorId] = useState('');
  const [quantity, setQuantity] = useState(5);
  const [issued, setIssued] = useState([]);
  const [existing, setExisting] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get('/admin/gift-cards', { params: { limit: 50 } });
      setExisting(data.gift_cards || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const issue = async () => {
    try {
      const { data } = await api.post('/admin/gift-cards', {
        amount: Number(amount), currency, quantity: Number(quantity),
        vendor_id: vendorId.trim() || undefined,
      });
      setIssued(data.codes || []);
      toast.success(`Issued ${data.issued} gift card(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Issue failed'); }
  };
  return (
    <section className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="giftcards-panel">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3">
        <Input value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" data-testid="gc-amount" />
        <Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} placeholder="CAD" data-testid="gc-currency" />
        <Input value={vendorId} onChange={(e) => setVendorId(e.target.value)} placeholder="Vendor id (blank=platform)" data-testid="gc-vendor" />
        <Input value={quantity} onChange={(e) => setQuantity(e.target.value)} placeholder="Quantity" data-testid="gc-qty" />
        <Button onClick={issue} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="gc-issue-btn">
          <Gift className="h-4 w-4 mr-1" /> Issue
        </Button>
      </div>
      {issued.length > 0 && (
        <div className="mb-3 p-3 rounded-xl border border-emerald-200 bg-emerald-50" data-testid="gc-new-list">
          <div className="text-xs font-medium text-emerald-800 mb-1">New codes (copy & share):</div>
          <div className="font-mono text-xs space-y-0.5">
            {issued.map((c) => <div key={c}>{c}</div>)}
          </div>
        </div>
      )}
      <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">Recent gift cards</div>
      <table className="w-full text-sm" data-testid="gc-existing-list">
        <thead className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
          <tr><th className="pb-2">Code</th><th className="pb-2">Amount</th><th className="pb-2">Scope</th><th className="pb-2">Redeemed</th><th className="pb-2">Created</th></tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {existing.map((c) => (
            <tr key={c.code}>
              <td className="py-2 font-mono text-xs">{c.code}</td>
              <td className="py-2">{c.amount} {c.currency}</td>
              <td className="py-2 text-xs">{c.vendor_id ? `Vendor: ${c.vendor_id}` : 'Platform'}</td>
              <td className="py-2 text-xs">{c.redeemed_by ? `by ${c.redeemed_by.slice(0, 12)}…` : <span className="text-emerald-600">Available</span>}</td>
              <td className="py-2 text-[11px] text-slate-500">{new Date(c.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

// ---------- Raffle ----------
const RafflePanel = () => {
  const [current, setCurrent] = useState(null);
  const [draws, setDraws] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [period, setPeriod] = useState('');
  const load = async () => {
    try {
      const { data } = await api.get('/admin/raffle/current', period ? { params: { period } } : undefined);
      setCurrent(data);
      const dr = await api.get('/admin/raffle/draws');
      setDraws(dr.data.draws || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  const runDraw = async () => {
    if (!window.confirm(`Run monthly raffle draw${period ? ` for ${period}` : ' (previous month)'}? This cannot be undone.`)) return;
    setDrawing(true);
    try {
      const { data } = await api.post('/admin/raffle/draw', { period: period || undefined });
      toast.success(`Draw complete — ${data.winners?.length || 0} winner(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Draw failed'); }
    finally { setDrawing(false); }
  };
  return (
    <section className="space-y-4" data-testid="raffle-panel">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="h-5 w-5 text-amber-500" />
          <h2 className="font-semibold text-slate-900">Current period</h2>
        </div>
        <div className="flex items-center gap-3 mb-3">
          <div className="text-sm text-slate-600">
            <div>Period: <b className="font-mono">{current?.period || '—'}</b></div>
            <div>Tickets: <b data-testid="raffle-ticket-count">{current?.ticket_count ?? 0}</b></div>
          </div>
          <Input value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="YYYY-MM (optional)" className="max-w-40 h-9" data-testid="raffle-period" />
          <Button variant="outline" size="sm" onClick={load}>Reload</Button>
          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white ml-auto" onClick={runDraw} disabled={drawing} data-testid="raffle-draw-btn">
            {drawing ? <><Loader2 className="h-3 w-3 animate-spin mr-1"/> Rolling…</> : <><DollarSign className="h-3 w-3 mr-1"/> Run monthly draw</>}
          </Button>
        </div>
        {current?.draw && (
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid="raffle-current-winners">
            <div className="text-xs font-medium text-amber-900 mb-1">Winners for {current.period}:</div>
            {(current.draw.winners || []).map((w) => (
              <div key={w.ticket_id} className="text-sm">
                {w.label}: <b>{w.currency} {w.amount}</b> — user <span className="font-mono text-xs">{w.user_id}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h2 className="font-semibold text-slate-900 mb-2">Past draws</h2>
        {draws.length === 0 ? <div className="text-sm text-slate-400">No completed draws yet.</div> : (
          <div className="divide-y divide-slate-100" data-testid="raffle-draw-history">
            {draws.map((d) => (
              <div key={d.id} className="py-2">
                <div className="text-sm font-medium">{d.period} <span className="text-xs text-slate-500">· {d.ticket_count} ticket(s)</span></div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {(d.winners || []).map((w, i) => (
                    <span key={i} className="mr-3">{w.label}: {w.currency}{w.amount}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default OperationsAdminPage;
